import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import SectionCard from "./SectionCard";
import SettingRow from "./SettingRow";
import { WEB_CONFIG_LABELS } from "../../datas/webConfigLabels";

type Json = null | boolean | number | string | Json[] | { [k: string]: Json };
type JsonObject = { [k: string]: Json };

interface Props {
  open: boolean;
  config: JsonObject;
  onClose: () => void;
  onSave: (patch: JsonObject) => Promise<void>;
}

const isObject = (v: Json): v is JsonObject => typeof v === "object" && v !== null && !Array.isArray(v);

// 불변 업데이트: path 위치의 값만 교체
const setIn = (obj: JsonObject, path: string[], value: Json): JsonObject => {
  const [head, ...rest] = path;
  return { ...obj, [head]: rest.length ? setIn(obj[head] as JsonObject, rest, value) : value };
};

// next 중 base 와 달라진 leaf 만 남긴 객체 (배열은 통째로 비교)
const diffConfig = (base: JsonObject, next: JsonObject): JsonObject =>
  Object.entries(next).reduce<JsonObject>((acc, [k, v]) => {
    if (isObject(v) && isObject(base[k])) {
      const sub = diffConfig(base[k] as JsonObject, v);
      if (Object.keys(sub).length) acc[k] = sub;
    } else if (JSON.stringify(v) !== JSON.stringify(base[k])) {
      acc[k] = v;
    }
    return acc;
  }, {});

const labelOf = (path: string[]) => WEB_CONFIG_LABELS[path.join(".")]?.label ?? path[path.length - 1];
const descOf = (path: string[]) => WEB_CONFIG_LABELS[path.join(".")]?.desc;

// 검색어가 키 경로 또는 한글 이름/설명에 포함된 leaf가 하나라도 있는지
const hasMatch = (value: Json, path: string[], q: string): boolean =>
  isObject(value)
    ? Object.entries(value).some(([k, v]) => hasMatch(v, [...path, k], q))
    : [path.join("."), labelOf(path), descOf(path) ?? ""].some((t) => t.toLowerCase().includes(q));

// 한글 설명 + 원본 키 경로
const Description = ({ path }: { path: string[] }) => (
  <>
    {descOf(path)}
    <Typography component="span" display="block" fontSize={10} color="text.disabled" fontFamily="monospace">
      {path.join(".")}
    </Typography>
  </>
);

export default function WebConfigModal({ open, config, onClose, onSave }: Props) {
  const [draft, setDraft] = useState(config);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDraft(config);
      setError(null);
    }
  }, [open, config]);

  const patch = useMemo(() => diffConfig(config, draft), [draft, config]);
  const dirty = Object.keys(patch).length > 0;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(patch);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setSaving(false);
    }
  };
  const q = query.trim().toLowerCase();
  const update = (path: string[], v: Json) => setDraft((d) => setIn(d, path, v));

  const renderNode = (key: string, value: Json, path: string[], depth: number): React.ReactNode => {
    if (q && !hasMatch(value, path, q)) return null;

    if (isObject(value)) {
      return (
        <Box key={key}>
          <Typography
            fontSize={11}
            fontWeight={600}
            color="text.secondary"
            sx={{ px: 2, pt: 1.5, pb: 0.5, pl: 2 + depth * 2, bgcolor: "grey.50" }}
          >
            {labelOf(path)}
            <Typography component="span" fontSize={10} color="text.disabled" fontFamily="monospace" ml={1}>
              {key}
            </Typography>
          </Typography>
          <Box sx={{ pl: depth * 2 }}>
            {Object.entries(value).map(([k, v]) => renderNode(k, v, [...path, k], depth + 1))}
          </Box>
        </Box>
      );
    }

    return (
      <SettingRow key={key} label={labelOf(path)} description={<Description path={path} />}>
        <LeafControl value={value} onChange={(v) => update(path, v)} />
      </SettingRow>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: "85vh" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1.5 }}>
        <Typography fontSize={16} fontWeight={500} sx={{ flex: 1 }}>
          전체 옵션 (web-config)
        </Typography>
        <TextField
          size="small"
          placeholder="옵션 검색"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16 }} />
              </InputAdornment>
            ),
            sx: { fontSize: 13 },
          }}
          sx={{ width: 240 }}
        />
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: "auto", "& > * + *": { mt: 1.5 } }}>
        {Object.entries(draft).map(([section, value]) => {
          if (q && !hasMatch(value, [section], q)) return null;
          return (
            <SectionCard
              key={section}
              icon={<TuneIcon fontSize="inherit" />}
              title={`${labelOf([section])}  ·  ${section}`}
              // 검색 중에는 매칭된 섹션 자동 펼침
              expanded={!!q || !!expanded[section]}
              onExpandChange={(v) => setExpanded((p) => ({ ...p, [section]: v }))}
            >
              {isObject(value) ? (
                Object.entries(value).map(([k, v]) => renderNode(k, v, [section, k], 0))
              ) : (
                <SettingRow label={labelOf([section])} description={<Description path={[section]} />}>
                  <LeafControl value={value} onChange={(v) => update([section], v)} />
                </SettingRow>
              )}
            </SectionCard>
          );
        })}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Typography fontSize={12} color={error ? "error.main" : "warning.main"} sx={{ flex: 1 }}>
          {error ? `저장 실패: ${error}` : dirty ? "저장되지 않은 변경사항이 있습니다 (저장 후 새로고침 시 적용)" : ""}
        </Typography>
        <Button size="small" onClick={() => setDraft(config)} disabled={!dirty || saving}>
          되돌리기
        </Button>
        <Button size="small" variant="contained" onClick={handleSave} disabled={!dirty || saving}>
          {saving ? "저장 중..." : "저장"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function LeafControl({ value, onChange }: { value: Json; onChange: (v: Json) => void }) {
  if (typeof value === "boolean") {
    return <Switch size="small" checked={value} onChange={(e) => onChange(e.target.checked)} />;
  }

  if (typeof value === "number") {
    return (
      <TextField
        size="small"
        type="number"
        value={value}
        onChange={(e) => e.target.value !== "" && onChange(Number(e.target.value))}
        inputProps={{ step: "any", style: { fontSize: 12, textAlign: "right" } }}
        sx={{ width: 120 }}
      />
    );
  }

  if (Array.isArray(value)) {
    // ponytail: 문자열 배열만 가정(LABELS 등), 객체 배열이 생기면 별도 에디터 필요
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "flex-end", maxWidth: 320 }}>
        {value.map((item, i) => (
          <Chip
            key={`${item}-${i}`}
            size="small"
            label={String(item)}
            onDelete={() => onChange(value.filter((_, j) => j !== i))}
          />
        ))}
        <TextField
          size="small"
          placeholder="추가 후 Enter"
          inputProps={{ style: { fontSize: 12 } }}
          sx={{ width: 110 }}
          onKeyDown={(e) => {
            const input = e.target as HTMLInputElement;
            if (e.key === "Enter" && input.value.trim()) {
              onChange([...value, input.value.trim()]);
              input.value = "";
            }
          }}
        />
      </Box>
    );
  }

  // string | null — 빈 문자열은 원래 null이었으면 null로 유지
  return (
    <TextField
      size="small"
      value={value ?? ""}
      placeholder={value === null ? "null" : ""}
      onChange={(e) => onChange(e.target.value === "" && value === null ? null : e.target.value)}
      inputProps={{ style: { fontSize: 12 } }}
      sx={{ width: 220 }}
    />
  );
}
